import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";

import {
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Field } from "@/components/cinch/Field";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useUser } from "@/lib/auth/useUser";

import {
  useAIReview,
  useAutoCategorize,
  useCreateCategory,
  useCreateNote,
  useDeleteCategory,
  useMoveNote,
  usePostNote,
  usePromoteCategory,
  useSummarizeCategory,
  useUpdateCategory,
  type Category,
  type Note,
  type Workspace,
} from "../api";
import { BOARD_META, type SwotBoard } from "../constants";
import { NoteCard } from "./NoteCard";
import { PostNoteDialog } from "./PostNoteDialog";
import { SwotNoteDialog } from "./SwotNoteDialog";

const DRAG_MIME = "application/x-planning-note";

/**
 * One SWOT wall: the author's private drafts on the left ("written on the
 * side"), swim lanes on the canvas. Notes drag between lanes natively; the AI
 * can sweep uncategorized notes into themes and write lane summaries.
 */
export function SwotBoardView({
  workspace,
  board,
  highlight = null,
}: {
  workspace: Workspace;
  board: SwotBoard;
  /** `data-planning-hl` key of a search-result target to shake/halo. */
  highlight?: string | null;
}) {
  const user = useUser();
  const sessionId = workspace.session.id;
  const move = useMoveNote(sessionId);
  const post = usePostNote(sessionId);
  const review = useAIReview();
  const autoCategorize = useAutoCategorize(sessionId);
  const createCategory = useCreateCategory(sessionId);

  const [posting, setPosting] = useState<Note | null>(null);
  const [detail, setDetail] = useState<Note | null>(null);
  const [newLaneOpen, setNewLaneOpen] = useState(false);
  const [newLaneName, setNewLaneName] = useState("");
  const [postingAll, setPostingAll] = useState(false);

  const notes = useMemo(
    () => workspace.notes.filter((n) => n.board === board),
    [workspace.notes, board],
  );
  const drafts = notes.filter((n) => n.status === "draft" && n.author_id === user.id);
  const posted = notes.filter((n) => n.status === "posted");
  const lanes = useMemo(
    () => workspace.categories.filter((c) => c.board === board),
    [workspace.categories, board],
  );
  const uncategorized = posted.filter(
    (n) => !n.category_id || !lanes.some((l) => l.id === n.category_id),
  );

  const onDropToLane = (e: DragEvent, lane: Category | null) => {
    e.preventDefault();
    const noteId = e.dataTransfer.getData(DRAG_MIME);
    if (!noteId) return;
    const dragged = workspace.notes.find((n) => n.id === noteId);
    if (dragged?.status === "draft") {
      // Dragging a draft onto the wall is a deliberate placement — post it
      // straight into the lane, no AI dialog.
      post.mutate(
        { id: noteId, board, category_id: lane?.id ?? null },
        {
          onSuccess: () => toast.success(lane ? `Posted to ${lane.name}` : "Posted to the wall"),
          onError: (err) => toast.error(err.message),
        },
      );
      return;
    }
    move.mutate(
      lane
        ? { id: noteId, category_id: lane.id }
        : { id: noteId, clear_category: true },
      { onError: (err) => toast.error(err.message) },
    );
  };

  const meta = BOARD_META[board];

  // ── Edge auto-scroll while dragging ──
  // Native HTML5 drag doesn't scroll inner overflow containers, so a card
  // couldn't reach an off-screen lane. A document-level dragover listener
  // (the strip itself stops getting events once the pointer passes its edge)
  // scrolls the strip horizontally at either side edge, and the page (the app
  // shell's <main>) vertically at the top/bottom edges — so holding a card
  // near the top scrolls up until the lane headers are in view. Speed ramps
  // the further past the edge; the rAF loop kills itself when dragover events
  // stop arriving (drop, cancel, or drag left the window).
  const laneStripRef = useRef<HTMLDivElement>(null);
  const edgeX = useRef(0);
  const edgeY = useRef(0);
  const edgeRaf = useRef<number | null>(null);
  const lastDragOver = useRef(0);

  useEffect(() => {
    const EDGE = 60; // trigger band inside an edge…
    const OUTSIDE = 400; // …and this far beyond it still counts

    const loop = () => {
      const strip = laneStripRef.current;
      const main = strip?.closest("main");
      const stale = performance.now() - lastDragOver.current > 250;
      if (!strip || stale || (edgeX.current === 0 && edgeY.current === 0)) {
        edgeRaf.current = null;
        return;
      }
      if (edgeX.current !== 0) strip.scrollLeft += edgeX.current;
      if (edgeY.current !== 0 && main) main.scrollTop += edgeY.current;
      edgeRaf.current = requestAnimationFrame(loop);
    };

    const onDragOver = (e: globalThis.DragEvent) => {
      const strip = laneStripRef.current;
      if (!strip) return;
      lastDragOver.current = performance.now();
      const r = strip.getBoundingClientRect();
      const main = strip.closest("main");
      const mr = main?.getBoundingClientRect();

      // Horizontal: only while the drag is roughly at the strip's height.
      let x = 0;
      if (e.clientY >= r.top - 100 && e.clientY <= r.bottom + 100) {
        if (e.clientX >= r.right - EDGE && e.clientX <= r.right + OUTSIDE) {
          x = 8 + Math.min(24, (e.clientX - (r.right - EDGE)) / 6);
        } else if (e.clientX <= r.left + EDGE && e.clientX >= r.left - OUTSIDE) {
          x = -(8 + Math.min(24, (r.left + EDGE - e.clientX) / 6));
        }
      }

      // Vertical: near the top/bottom of the visible page area.
      let y = 0;
      if (mr) {
        if (e.clientY <= mr.top + EDGE + 20 && e.clientY >= mr.top - OUTSIDE) {
          y = -(8 + Math.min(24, (mr.top + EDGE + 20 - e.clientY) / 6));
        } else if (e.clientY >= mr.bottom - EDGE && e.clientY <= mr.bottom + OUTSIDE) {
          y = 8 + Math.min(24, (e.clientY - (mr.bottom - EDGE)) / 6);
        }
      }

      edgeX.current = x;
      edgeY.current = y;
      if ((x !== 0 || y !== 0) && edgeRaf.current === null) {
        edgeRaf.current = requestAnimationFrame(loop);
      }
    };

    document.addEventListener("dragover", onDragOver);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      if (edgeRaf.current !== null) cancelAnimationFrame(edgeRaf.current);
    };
  }, []);

  const postAll = async () => {
    setPostingAll(true);
    const ids = drafts.map((d) => d.id);
    let ok = 0;
    for (const id of ids) {
      try {
        await post.mutateAsync({ id, board, category_id: null });
        ok += 1;
      } catch (err) {
        toast.error(err instanceof Error ? err.message : String(err));
      }
    }
    setPostingAll(false);
    if (ok) toast.success(`${ok} note${ok === 1 ? "" : "s"} posted — the AI will flag any it doubts`);
    // Advisory reviews in the background, one at a time (the model host queues
    // anyway); a flagged note gets its badge via the poll. First failure means
    // Ollama is down — stop quietly, posting already succeeded.
    void (async () => {
      for (const id of ids) {
        try {
          await review.mutateAsync(id);
        } catch {
          break;
        }
      }
    })();
  };

  return (
    <div className="flex gap-4">
      {/* ── Drafts: private until posted ── */}
      <aside className="w-56 shrink-0 space-y-3">
        <DraftComposer workspace={workspace} board={board} />
        {drafts.length ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                My drafts ({drafts.length})
              </span>
              <Button
                size="sm"
                variant="secondary"
                className="h-6 px-2 text-[11px]"
                disabled={postingAll || post.isPending}
                onClick={postAll}
              >
                {postingAll ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-1 h-3 w-3" />
                )}
                Post all
              </Button>
            </div>
            {drafts.map((n) => (
              <div key={n.id} className="space-y-1">
                <NoteCard
                  note={n}
                  size="sm"
                  data-planning-hl={`note-${n.id}`}
                  className={cn(
                    "w-full cursor-grab active:cursor-grabbing",
                    highlight === `note-${n.id}` && "planning-highlight",
                  )}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(DRAG_MIME, n.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onClick={() => setDetail(n)}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 w-full text-xs"
                  onClick={() => setPosting(n)}
                >
                  <Send className="mr-1 h-3 w-3" /> Post to wall
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Drafts are only visible to you until you post them — use the button
            for the AI gut-check, or drag one straight onto a lane.
          </p>
        )}
      </aside>

      {/* ── The wall ── */}
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{meta.hint}</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => setNewLaneOpen(true)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Lane
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={autoCategorize.isPending || uncategorized.length === 0}
              onClick={() =>
                autoCategorize.mutate(board, {
                  onSuccess: (r) =>
                    toast.success(
                      r.assigned
                        ? `Sorted ${r.assigned} notes${r.created_categories.length ? ` · new lanes: ${r.created_categories.join(", ")}` : ""}`
                        : "Nothing to sort",
                    ),
                  onError: (err) => toast.error(err.message),
                })
              }
            >
              {autoCategorize.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="mr-1 h-3.5 w-3.5" />
              )}
              AI categorize
            </Button>
          </div>
        </div>

        <div ref={laneStripRef} className="flex gap-3 overflow-x-auto pb-2">
          <Lane
            title="Uncategorized"
            notes={uncategorized}
            onDrop={(e) => onDropToLane(e, null)}
            onNoteClick={setDetail}
            highlight={highlight}
          />
          {lanes.map((lane) => (
            <CategoryLane
              key={lane.id}
              lane={lane}
              notes={posted.filter((n) => n.category_id === lane.id)}
              workspace={workspace}
              onDrop={(e) => onDropToLane(e, lane)}
              onNoteClick={setDetail}
              highlight={highlight}
            />
          ))}
        </div>
      </div>

      <PostNoteDialog
        note={posting}
        workspace={workspace}
        onOpenChange={(open) => !open && setPosting(null)}
      />
      <SwotNoteDialog
        note={detail ? (workspace.notes.find((n) => n.id === detail.id) ?? null) : null}
        workspace={workspace}
        onOpenChange={(open) => !open && setDetail(null)}
      />

      <Dialog open={newLaneOpen} onOpenChange={setNewLaneOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New lane on {meta.label}</DialogTitle>
          </DialogHeader>
          <Field
            id="new-lane-name"
            label="Lane name"
            value={newLaneName}
            onChange={(e) => setNewLaneName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewLaneOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!newLaneName.trim() || createCategory.isPending}
              onClick={() =>
                createCategory.mutate(
                  { board, name: newLaneName.trim() },
                  {
                    onSuccess: () => {
                      setNewLaneOpen(false);
                      setNewLaneName("");
                    },
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DraftComposer({ workspace, board }: { workspace: Workspace; board: SwotBoard }) {
  const create = useCreateNote(workspace.session.id);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const titleRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    if (!title.trim()) return;
    create.mutate(
      { board, title: title.trim(), description: description.trim() || null },
      {
        onSuccess: () => {
          setTitle("");
          setDescription("");
          // Straight back to the title so a burst of notes flows without the mouse.
          titleRef.current?.focus();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Write a note
      </div>
      <Field
        ref={titleRef}
        id={`draft-title-${board}`}
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
      />
      <Textarea
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
      />
      <Button
        size="sm"
        className="w-full"
        disabled={!title.trim() || create.isPending}
        onClick={submit}
      >
        <Plus className="mr-1 h-3.5 w-3.5" /> Add draft
      </Button>
    </div>
  );
}

function Lane({
  title,
  notes,
  onDrop,
  onNoteClick,
  header,
  summary,
  highlight = null,
  hlKey,
}: {
  title: string;
  notes: Note[];
  onDrop: (e: DragEvent) => void;
  onNoteClick: (note: Note) => void;
  header?: React.ReactNode;
  summary?: React.ReactNode;
  highlight?: string | null;
  /** This lane's own `data-planning-hl` key (search "Lane" results). */
  hlKey?: string;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      data-planning-hl={hlKey}
      className={cn(
        "flex w-60 shrink-0 flex-col rounded-md border bg-muted/30",
        over && "border-brand bg-brand/5",
        hlKey && highlight === hlKey && "planning-highlight",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        onDrop(e);
      }}
    >
      <div className="flex items-center justify-between gap-1 border-b px-2.5 py-2">
        <div className="truncate text-xs font-semibold">
          {title} <span className="font-normal text-muted-foreground">({notes.length})</span>
        </div>
        {header}
      </div>
      {summary}
      <div className="flex flex-1 flex-col items-center gap-2 p-2.5">
        {notes.map((n) => (
          <NoteCard
            key={n.id}
            note={n}
            size="sm"
            data-planning-hl={`note-${n.id}`}
            className={cn(
              "w-full cursor-grab active:cursor-grabbing",
              highlight === `note-${n.id}` && "planning-highlight",
            )}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_MIME, n.id);
              e.dataTransfer.effectAllowed = "move";
            }}
            onClick={() => onNoteClick(n)}
          />
        ))}
        {notes.length === 0 ? (
          <div className="py-6 text-center text-[11px] text-muted-foreground">Drop notes here</div>
        ) : null}
      </div>
    </div>
  );
}

function CategoryLane({
  lane,
  notes,
  workspace,
  onDrop,
  onNoteClick,
  highlight = null,
}: {
  lane: Category;
  notes: Note[];
  workspace: Workspace;
  onDrop: (e: DragEvent) => void;
  onNoteClick: (note: Note) => void;
  highlight?: string | null;
}) {
  const sessionId = workspace.session.id;
  const summarize = useSummarizeCategory(sessionId);
  const promote = usePromoteCategory(sessionId);
  const updateCategory = useUpdateCategory(sessionId);
  const deleteCategory = useDeleteCategory(sessionId);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(lane.name);

  const promoted = workspace.notes.some(
    (n) => n.source_category_id === lane.id && n.board === "impact_effort",
  );

  return (
    <>
      <Lane
        title={lane.name}
        notes={notes}
        onDrop={onDrop}
        onNoteClick={onNoteClick}
        highlight={highlight}
        hlKey={`lane-${lane.id}`}
        header={
          <div className="flex items-center gap-0.5">
            {summarize.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={notes.length === 0 || summarize.isPending}
                  onClick={() =>
                    summarize.mutate(lane.id, {
                      onSuccess: () => {
                        setSummaryOpen(true);
                        toast.success("Summary written");
                      },
                      onError: (err) => toast.error(err.message),
                    })
                  }
                >
                  <Sparkles className="mr-2 h-3.5 w-3.5" /> AI summary
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={promoted || promote.isPending}
                  onClick={() =>
                    promote.mutate(
                      { id: lane.id },
                      {
                        onSuccess: () => toast.success("Sent to the Impact vs Effort grid"),
                        onError: (err) => toast.error(err.message),
                      },
                    )
                  }
                >
                  <ArrowUpRight className="mr-2 h-3.5 w-3.5" />
                  {promoted ? "Already on the grid" : "Promote to grid"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setRenameValue(lane.name);
                    setRenameOpen(true);
                  }}
                >
                  <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() =>
                    deleteCategory.mutate(lane.id, {
                      onSuccess: () => toast.success("Lane removed (notes kept)"),
                      onError: (err) => toast.error(err.message),
                    })
                  }
                >
                  <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete lane
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        summary={
          lane.summary ? (
            <button
              type="button"
              className="w-full border-b bg-brand/5 px-2.5 py-1.5 text-left"
              onClick={() => setSummaryOpen((v) => !v)}
            >
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand">
                {summaryOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                AI summary
              </span>
              {summaryOpen ? (
                <p className="mt-1 whitespace-pre-wrap text-[11px] leading-snug text-foreground/80">
                  {lane.summary}
                </p>
              ) : null}
            </button>
          ) : undefined
        }
      />

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename lane</DialogTitle>
          </DialogHeader>
          <Field
            id={`rename-lane-${lane.id}`}
            label="Lane name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!renameValue.trim() || updateCategory.isPending}
              onClick={() =>
                updateCategory.mutate(
                  { id: lane.id, name: renameValue.trim() },
                  {
                    onSuccess: () => setRenameOpen(false),
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
